import * as React from 'react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import ActionButtons from './ActionButtons';
import { showModal } from '@actions/common/actionCreators';
import { configs } from '@constants/configs/configs';
import { dashboardModalTypes } from '@constants/individualPortal/dashboard/modals/dashboardModalTypes';
import useTobaccoExemptData from './useTobaccoExempt';
import TobaccoAlert from './TobaccoAlert';
import TobaccoExemptHeader from './TobaccoExemptHeader';
import HardshipExemptionForm from './HardshipExemptionForm';
import GITextIntl from '@components/common/app-components/GIText/GITextIntl';
import { ConditionalRendering } from '@components/common/conditional-rendering';
import { IDataToSubmit } from './types/apiResponse';
import ScrollableReadOnlyText from '@components/common/readOnlyText/ScrollableReadOnlyText';
import { disclaimerMessages } from './utils/app.constants';
import Spinner from '@components/common/spinner/Spinner';

const TobaccoExemptContainer = () => {
    const reduxDispatch = useDispatch();
    const navigate = useNavigate();
    // Use the hook and extract properties for handling the tobacco exemption data.
    const {
        additionalInfoData,
        caseNumber,
        environment,
        getAdditionalInfo,
        isDisclaimerVisible,
        isHardshipExemptCheckBoxVisible,
        isSavingError,
        onSaveAndContinue,
        updateTobaccoUse,
        updateExemptionNumber,
        updateHardExemption,
        updateHasDisability,
    } = useTobaccoExemptData({
        navigate,
        dispatch: reduxDispatch,
    });

    const {
        stateCode,
        screenerConfigs: { iexCurrentCoverageYear, iexPreviousCoverageYear },
        exchangeName,
    } = environment;

    const { routes } = configs;
    // Destructure data for additional information and loading states.
    const { additionalInfo, isError } = additionalInfoData;
    // Extract eligibility details and check for existence.
    const eligibilityDetails = additionalInfo?.applicantDetails?.eligiblitydetails || [];
    // Extract and compute boolean flags based on the additionalInfo data.
    const isCoverageYearOver = additionalInfo?.coverageYearOver || false;
    const isHardExemptionChecked = additionalInfo?.householdExempt || false;
    const isEligibilityDetailsExist = eligibilityDetails.length > 0;

    useEffect(() => {
        if (caseNumber) {
            getAdditionalInfo();
        }
    }, [caseNumber]);

    // Open modal if there is an error ( isSavingError is true )
    useEffect(() => {
        if (isSavingError) {
            reduxDispatch(showModal({ modalData: {}, modalType: dashboardModalTypes.FAILURE_MODAL }));
        }
    }, [isSavingError, reduxDispatch]);

    const gotoDashboard = () => {
        navigate(routes.INDPORTAL);
    };

    const dataToSubmit: IDataToSubmit = {
        applicantDetails: {
            eligiblitydetails: eligibilityDetails,
            ...(additionalInfo?.applicantDetails?.inEligibilitydetails && {
                inEligibilitydetails: additionalInfo.applicantDetails.inEligibilitydetails,
            }),
        },
        householdExempt: isHardExemptionChecked,
        isChangePlanFlow: additionalInfo?.isChangePlanFlow || false,
        isSEPPlanFlow: additionalInfo?.isSEPPlanFlow || false,
    };
    const onHandleSaveAndContinue = async () => {
        try {
            await onSaveAndContinue(dataToSubmit, isHardExemptionChecked, eligibilityDetails);
        } catch (error) {
            console.error('Error saving data:', error);
        }
    };
    if (isError) {
        return <TobaccoAlert messageId="indportal.tobacco.additionalInfo.error" id="additional-info-error" />;
    }
    if (isCoverageYearOver) {
        return (
            <TobaccoAlert
                messageId="indportal.tobacco.coverageYearOver.error"
                id="coverageYearOver-error"
                textReplacements={{
                    currentCoverageYear: iexCurrentCoverageYear,
                    previousCoverageYear: iexPreviousCoverageYear,
                    b: chunks => <strong>{chunks}</strong>,
                }}
            />
        );
    }
    if (!additionalInfo) {
        return <Spinner message="Loading..." />;
    }
    return (
        <>
            <div className="grid-row" id="tobacco-exempt">
                <div className="grid-col-12 tablet:grid-col-12">
                    <TobaccoExemptHeader />
                    <div id={'body-wrapper'} className={'body-wrapper'}>
                        <GITextIntl
                            id={'additional-info__need-details'}
                            messageId={'indportal.tobacco.info.needDetails'}
                            textReplacements={{ exchangeName, br: <br /> }}
                        />
                        <ConditionalRendering isTrue={additionalInfo?.showTobaccoCheckBox}>
                            <TobaccoAlert
                                messageId={'indportal.tobacco.alert.tobaccoUseContent'}
                                id={'tobacco-use-content'}
                                textReplacements={{
                                    strong: chunks => <strong>{chunks}</strong>,
                                }}
                            />
                        </ConditionalRendering>
                        <ConditionalRendering isTrue={isHardshipExemptCheckBoxVisible}>
                            <TobaccoAlert
                                messageId={'indportal.tobacco.alert.hardshipExemptContent'}
                                id={'hardship-exempt-content'}
                                textReplacements={{
                                    strong: chunks => <strong>{chunks}</strong>,
                                    a: chunks => (
                                        <a
                                            href={configs.endpoints.externalUrls.hardshipExemptContentLink[stateCode]}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {chunks}
                                        </a>
                                    ),
                                }}
                            />
                        </ConditionalRendering>
                        <ConditionalRendering isTrue={additionalInfo?.showBlindOrDisabilityCheckBox}>
                            <TobaccoAlert
                                messageId={'indportal.tobacco.alert.hasDisabilityContent'}
                                id={'blind-or-disability-content'}
                                textReplacements={{
                                    strong: chunks => <strong>{chunks}</strong>,
                                }}
                            />
                        </ConditionalRendering>

                        <HardshipExemptionForm
                            additionalInfo={additionalInfo}
                            isHardExemptionChecked={isHardExemptionChecked}
                            isHardshipExemptCheckBoxVisible={isHardshipExemptCheckBoxVisible}
                            onHandleExemptionNumberChange={updateExemptionNumber}
                            onHandleHardExemptionChange={checked => updateHardExemption(checked)}
                            onHandleTobaccoUseChange={personId => updateTobaccoUse(personId)}
                            onHandleHasDisabilityChange={personId => updateHasDisability(personId)}
                            stateCode={stateCode}
                            eligibilityDetails={eligibilityDetails}
                        />

                        <ConditionalRendering isTrue={isDisclaimerVisible}>
                            <div className={'tobacco-exempt-privacyNotice-container'}>
                                <GITextIntl
                                    id={'tobacco-exempt-privacyNotice-info'}
                                    messageId={'indportal.tobacco.privacyNotice.info'}
                                />
                            </div>
                            <ScrollableReadOnlyText id={'tobacco-exempt-disclaimer'} messageIds={disclaimerMessages} />
                        </ConditionalRendering>
                        <ActionButtons
                            isCoverageYearOver={isCoverageYearOver}
                            gotoDashboard={gotoDashboard}
                            isEligibilityDetailsExist={isEligibilityDetailsExist}
                            onSaveAndContinue={onHandleSaveAndContinue}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

export default TobaccoExemptContainer;
